import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ColorLensIcon from "@mui/icons-material/ColorLens";

import {
  createStage,
  updateStage,
  deleteStage,
  reorderStages,
} from "../../api/endpoints/enrollmentCrm";

const StagesSettingsDialog = ({
  open,
  onClose,
  stages = [],
  onSaved,
  isAdmin = false,
}) => {
  const sortedStages = useMemo(() => {
    return [...(stages || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [stages]);

  const [localStages, setLocalStages] = useState(sortedStages);
  const [loading, setLoading] = useState(false);

  const [newStage, setNewStage] = useState({
    name: "",
    color: "#3498db",
    isComplete: false,
  });

  useEffect(() => {
    if (!open) return;
    setLocalStages(sortedStages);
    setNewStage({ name: "", color: "#3498db", isComplete: false });
    setLoading(false);
  }, [open, sortedStages]);

  const persistOrder = async (stagesOrder) => {
    // stagesOrder: [{id, order}]
    await reorderStages(stagesOrder);
    if (onSaved) await onSaved();
  };

  const handleMove = async (index, delta) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= localStages.length) return;

    const updated = [...localStages];
    const [removed] = updated.splice(index, 1);
    updated.splice(nextIndex, 0, removed);

    const stagesOrder = updated.map((s, i) => ({ id: s.id, order: i }));
    setLocalStages(updated.map((s, i) => ({ ...s, order: i })));

    setLoading(true);
    try {
      await persistOrder(stagesOrder);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStage = async () => {
    if (!newStage.name.trim()) return;
    setLoading(true);
    try {
      await createStage({
        name: newStage.name.trim(),
        color: newStage.color,
        isComplete: Boolean(newStage.isComplete),
        order: localStages.length,
        active: true,
      });
      setNewStage({ name: "", color: "#3498db", isComplete: false });
      if (onSaved) await onSaved();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStage = async (stageId, patch) => {
    setLoading(true);
    try {
      await updateStage(stageId, patch);
      if (onSaved) await onSaved();
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm("Excluir estágio?")) return;
    setLoading(true);
    try {
      await deleteStage(stageId);
      if (onSaved) await onSaved();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Configurações de Estágios</DialogTitle>
      <DialogContent dividers>
        {!isAdmin ? (
          <Typography variant="body1" align="center" sx={{ py: 3 }}>
            Somente administradores podem acessar esta funcionalidade.
          </Typography>
        ) : (
          <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Novo estágio
              </Typography>
              <TextField
                label="Nome"
                value={newStage.name}
                onChange={(e) => setNewStage((p) => ({ ...p, name: e.target.value }))}
                fullWidth
                margin="dense"
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                <ColorLensIcon color="action" />
                <TextField
                  label="Cor"
                  type="color"
                  value={newStage.color}
                  onChange={(e) => setNewStage((p) => ({ ...p, color: e.target.value }))}
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(newStage.isComplete)}
                      onChange={(e) => setNewStage((p) => ({ ...p, isComplete: e.target.checked }))}
                    />
                  }
                  label="É etapa final?"
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" onClick={handleAddStage} disabled={loading || !newStage.name.trim()}>
                  Adicionar
                </Button>
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Lista de estágios
            </Typography>
            <List dense>
              {localStages.map((stage, index) => (
                <ListItem key={stage.id} disableGutters sx={{ mb: 1, px: 0 }}>
                  <Paper sx={{ p: 2, width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: 1,
                          bgcolor: stage.color || "#3498db",
                          border: "1px solid rgba(0,0,0,0.08)",
                        }}
                      />
                      <Typography sx={{ minWidth: 34, fontWeight: 700, color: "text.secondary" }}>
                        {index + 1}
                      </Typography>
                      <TextField
                        size="small"
                        label="Nome"
                        value={stage.name}
                        onChange={(e) =>
                          setLocalStages((prev) =>
                            prev.map((s) => (s.id === stage.id ? { ...s, name: e.target.value } : s))
                          )
                        }
                        sx={{ flex: 1 }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                      <TextField
                        size="small"
                        label="Cor"
                        type="color"
                        value={stage.color || "#3498db"}
                        onChange={(e) =>
                          setLocalStages((prev) =>
                            prev.map((s) => (s.id === stage.id ? { ...s, color: e.target.value } : s))
                          )
                        }
                        sx={{ width: 110 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(stage.isComplete)}
                            onChange={(e) =>
                              setLocalStages((prev) =>
                                prev.map((s) => (s.id === stage.id ? { ...s, isComplete: e.target.checked } : s))
                              )
                            }
                          />
                        }
                        label="Final"
                      />
                      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton onClick={() => handleMove(index, -1)} disabled={loading || index === 0}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleMove(index, 1)}
                          disabled={loading || index === localStages.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            handleUpdateStage(stage.id, {
                              name: stage.name,
                              color: stage.color,
                              isComplete: Boolean(stage.isComplete),
                              active: true,
                              order: stage.order,
                            })
                          }
                          disabled={loading}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: 12 }}>Salvar</Typography>
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteStage(stage.id)}
                          disabled={loading}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </ListItem>
              ))}
              {localStages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Nenhum estágio cadastrado.
                </Typography>
              ) : null}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StagesSettingsDialog;

